import type { ReactNode } from 'react'
import type { CollectionBlock, CollectionSummary } from '../lib/types'
import { btn } from './ui'

/** "14 thoughts" / "1 thought". */
export function thoughtCount(n: number): string {
  return `${n} thought${n === 1 ? '' : 's'}`
}

/** A collection block snapshotting one of the author's collections (see CollectionBlock in lib/types). */
export function collectionSnapshot(c: CollectionSummary): Omit<CollectionBlock, 'id' | 'type'> {
  return {
    collectionId: c.id,
    name: c.name,
    count: c.thought_count,
    ...(c.cover_color ? { color: c.cover_color } : {}),
    ...(c.cover_url ? { cover: c.cover_url } : {}),
    ...(c.description?.trim() ? { description: c.description.trim() } : {}),
    // Only a public collection has a page readers can open.
    ...(c.is_public && c.public_slug ? { slug: c.public_slug } : {}),
  }
}

/** The collection's tile: its cover image, or its colour. */
export function CollectionTile({ color, cover, size }: { color?: string | null; cover?: string | null; size: number }) {
  return cover ? (
    <img src={cover} alt="" aria-hidden style={{ width: size, height: size }} className="shrink-0 rounded-xl object-cover" />
  ) : (
    <span aria-hidden style={{ width: size, height: size, background: color || 'var(--accent)' }} className="shrink-0 rounded-xl" />
  )
}

/**
 * The card body shared by the composer and the reader: tile, a small-caps
 * eyebrow, the name in the display face, the count, and an Explore button
 * when there's somewhere to go.
 */
export function CollectionCardBody({ block, author, explore, note }: { block: CollectionBlock; author?: string; explore: boolean; note?: ReactNode }) {
  return (
    <>
      <CollectionTile color={block.color} cover={block.cover} size={64} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">{author ? `A COLLECTION BY ${author.toUpperCase()}` : 'COLLECTION'}</span>
        <span className="font-display text-[20px]/[1.2] text-primary">{block.name}</span>
        {block.description && <span className="line-clamp-2 font-display text-[14px]/[1.45] text-foreground/65">{block.description}</span>}
        <span className="text-[13px] text-muted-foreground">
          {thoughtCount(block.count)}
          {note}
        </span>
      </span>
      {explore && <span className={btn.outlineLg}>Explore</span>}
    </>
  )
}

export const collectionCardCls =
  'clear-both flex flex-wrap items-center gap-[18px] rounded-xl border border-card-edge bg-card px-5 py-[18px] font-sans text-foreground no-underline sm:flex-nowrap'
